import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { matchPath } from 'react-router-dom';

/**
 * Every screen behind the dashboard shell, in one table.
 *
 * app.tsx used to spell each of these out as ~14 lines of nested
 * ProtectedRoute / DashboardLayout / breadcrumb JSX — 836 lines that had to be
 * edited in three places to add a page. The shell is now a layout route and
 * this is the only list.
 *
 * Each screen is imported lazily, so a route is downloaded when it is first
 * visited rather than shipping all sixty in the initial bundle. The screens
 * that have no default export are mapped by name here rather than being
 * touched.
 */

const AppContractsScreen = lazy(() =>
  import('./modules/settings/screens/app-contracts').then((m) => ({ default: m.AppContractsScreen }))
);
const AuditLogsScreen = lazy(() =>
  import('./modules/rbac/screens/audit-logs').then((m) => ({ default: m.AuditLogsScreen }))
);
const BlogListScreen = lazy(() =>
  import('./modules/blog/screens/blog-list').then((m) => ({ default: m.BlogListScreen }))
);
const CampaignCodesScreen = lazy(() =>
  import('./modules/campaigns/screens/campaign-codes').then((m) => ({ default: m.CampaignCodesScreen }))
);
const CampaignDetailsScreen = lazy(() =>
  import('./modules/campaigns/screens/campaign-details').then((m) => ({ default: m.CampaignDetailsScreen }))
);
const CampaignListScreen = lazy(() =>
  import('./modules/campaigns/screens/campaign-list').then((m) => ({ default: m.CampaignListScreen }))
);
const ChangelogScreen = lazy(() =>
  import('./modules/changelog/screens/changelog').then((m) => ({ default: m.ChangelogScreen }))
);
const CommunicationPermissionsScreen = lazy(() =>
  import('./modules/settings/screens/communication-permissions').then((m) => ({ default: m.CommunicationPermissionsScreen }))
);
const ContactInfoScreen = lazy(() =>
  import('./modules/settings/screens/contact-info').then((m) => ({ default: m.ContactInfoScreen }))
);
const ContractDetailsScreen = lazy(() =>
  import('./modules/contracts/screens/contract-details').then((m) => ({ default: m.ContractDetailsScreen }))
);
const ContractListScreen = lazy(() =>
  import('./modules/contracts/screens/contract-list').then((m) => ({ default: m.ContractListScreen }))
);
const CreateBlogScreen = lazy(() =>
  import('./modules/blog/screens/create-blog').then((m) => ({ default: m.CreateBlogScreen }))
);
const CreateCampaignScreen = lazy(() =>
  import('./modules/campaigns/screens/create-campaign').then((m) => ({ default: m.CreateCampaignScreen }))
);
const CreateExerciseScreen = lazy(() =>
  import('./modules/fit/screens/create-exercise').then((m) => ({ default: m.CreateExerciseScreen }))
);
const CreateExerciseTypeScreen = lazy(() =>
  import('./modules/fit/screens/create-exercise-type').then((m) => ({ default: m.CreateExerciseTypeScreen }))
);
const CreateLevelScreen = lazy(() =>
  import('./modules/fit/screens/create-level').then((m) => ({ default: m.CreateLevelScreen }))
);
const CreateNewsScreen = lazy(() =>
  import('./modules/news/screens/create-news').then((m) => ({ default: m.CreateNewsScreen }))
);
const CreateRoleScreen = lazy(() =>
  import('./modules/rbac/screens/create-role').then((m) => ({ default: m.CreateRoleScreen }))
);
const CreateTargetScreen = lazy(() =>
  import('./modules/fit/screens/create-target').then((m) => ({ default: m.CreateTargetScreen }))
);
const CreateVideoScreen = lazy(() =>
  import('./modules/fit/screens/create-video').then((m) => ({ default: m.CreateVideoScreen }))
);
const Dashboard = lazy(() =>
  import('./components/dashboard').then((m) => ({ default: m.Dashboard }))
);
const EarningsBreakdownScreen = lazy(() =>
  import('./modules/referrals/screens/earnings-breakdown').then((m) => ({ default: m.EarningsBreakdownScreen }))
);
const EditBlogScreen = lazy(() =>
  import('./modules/blog/screens/edit-blog').then((m) => ({ default: m.EditBlogScreen }))
);
const EditCampaignScreen = lazy(() =>
  import('./modules/campaigns/screens/edit-campaign').then((m) => ({ default: m.EditCampaignScreen }))
);
const EditExerciseScreen = lazy(() =>
  import('./modules/fit/screens/edit-exercise').then((m) => ({ default: m.EditExerciseScreen }))
);
const EditExerciseTypeScreen = lazy(() =>
  import('./modules/fit/screens/edit-exercise-type').then((m) => ({ default: m.EditExerciseTypeScreen }))
);
const EditLevelScreen = lazy(() =>
  import('./modules/fit/screens/edit-level').then((m) => ({ default: m.EditLevelScreen }))
);
const EditNewsScreen = lazy(() =>
  import('./modules/news/screens/edit-news').then((m) => ({ default: m.EditNewsScreen }))
);
const EditRoleScreen = lazy(() =>
  import('./modules/rbac/screens/edit-role').then((m) => ({ default: m.EditRoleScreen }))
);
const EditTargetScreen = lazy(() =>
  import('./modules/fit/screens/edit-target').then((m) => ({ default: m.EditTargetScreen }))
);
const EditVideoScreen = lazy(() =>
  import('./modules/fit/screens/edit-video').then((m) => ({ default: m.EditVideoScreen }))
);
const ExerciseListScreen = lazy(() =>
  import('./modules/fit/screens/exercise-list').then((m) => ({ default: m.ExerciseListScreen }))
);
const ExerciseTypeListScreen = lazy(() =>
  import('./modules/fit/screens/exercise-type-list').then((m) => ({ default: m.ExerciseTypeListScreen }))
);
const FaqCategoriesScreen = lazy(() =>
  import('./modules/settings/screens/faq-categories').then((m) => ({ default: m.FaqCategoriesScreen }))
);
const FaqManagementScreen = lazy(() =>
  import('./modules/settings/screens/faq-management').then((m) => ({ default: m.FaqManagementScreen }))
);
const FeaturedManagementScreen = lazy(() =>
  import('./modules/blog/screens/featured-management').then((m) => ({ default: m.FeaturedManagementScreen }))
);
const FinanceNewsManagementScreen = lazy(() =>
  import('./modules/finance/screens/news-management').then((m) => ({ default: m.FinanceNewsManagementScreen }))
);
const FinanceStatisticsScreen = lazy(() =>
  import('./modules/finance/screens/statistics').then((m) => ({ default: m.FinanceStatisticsScreen }))
);
const LevelListScreen = lazy(() =>
  import('./modules/fit/screens/level-list').then((m) => ({ default: m.LevelListScreen }))
);
const ManualPaymentScreen = lazy(() =>
  import('./modules/referrals/screens/manual-payment').then((m) => ({ default: m.ManualPaymentScreen }))
);
const NewsListScreen = lazy(() =>
  import('./modules/news/screens/news-list').then((m) => ({ default: m.NewsListScreen }))
);
const ProfileScreen = lazy(() =>
  import('./modules/user/screens/profile').then((m) => ({ default: m.ProfileScreen }))
);
const PushNotificationScreen = lazy(() =>
  import('./modules/settings/screens/push-notification').then((m) => ({ default: m.PushNotificationScreen }))
);
const ReferralDetailsScreen = lazy(() =>
  import('./modules/referrals/screens/referral-details').then((m) => ({ default: m.ReferralDetailsScreen }))
);
const ReferralListScreen = lazy(() =>
  import('./modules/referrals/screens/referral-list').then((m) => ({ default: m.ReferralListScreen }))
);
const RoleListScreen = lazy(() =>
  import('./modules/rbac/screens/roles').then((m) => ({ default: m.RoleListScreen }))
);
const SavingsPlanScreen = lazy(() =>
  import('./modules/contracts/screens/savings-plan').then((m) => ({ default: m.SavingsPlanScreen }))
);
const SavingsPlansListScreen = lazy(() =>
  import('./modules/contracts/screens/savings-plans-list').then((m) => ({ default: m.SavingsPlansListScreen }))
);
const SystemUsersScreen = lazy(() =>
  import('./modules/rbac/screens/system-users').then((m) => ({ default: m.SystemUsersScreen }))
);
const TargetListScreen = lazy(() =>
  import('./modules/fit/screens/target-list').then((m) => ({ default: m.TargetListScreen }))
);
const UserDetailsScreen = lazy(() =>
  import('./modules/users/screens/user-details').then((m) => ({ default: m.UserDetailsScreen }))
);
const UserEarningsScreen = lazy(() =>
  import('./modules/referrals/screens/user-earnings').then((m) => ({ default: m.UserEarningsScreen }))
);
const UserExerciseDetailsScreen = lazy(() =>
  import('./modules/fit/screens/user-exercise-details').then((m) => ({ default: m.UserExerciseDetailsScreen }))
);
const UserExerciseListScreen = lazy(() =>
  import('./modules/fit/screens/user-exercise-list').then((m) => ({ default: m.UserExerciseListScreen }))
);
const UserListScreen = lazy(() =>
  import('./modules/users/screens/user-list').then((m) => ({ default: m.UserListScreen }))
);
const VideoListScreen = lazy(() =>
  import('./modules/fit/screens/video-list').then((m) => ({ default: m.VideoListScreen }))
);

export interface AppRoute {
  path: string;
  /** Last breadcrumb segment, and the page's own name. */
  title: string;
  /** Non-navigable grouping segment, e.g. "RBAC" or "İçerik". */
  group?: string;
  /** Path of the page this one sits under; its breadcrumb becomes a link. */
  parent?: string;
  element: LazyExoticComponent<ComponentType>;
  /**
   * Sidebar entry is greyed out and the screen is not ready. The route stays —
   * a bookmark should land somewhere that explains itself rather than on a
   * half-built screen or a 404.
   */
  pending?: true;
}

export const APP_ROUTES: AppRoute[] = [
  { path: '/', title: 'Ana Sayfa', element: Dashboard },
  { path: '/dashboard', title: 'Ana Sayfa', element: Dashboard },
  { path: '/rbac/roles', title: 'Roller', group: 'RBAC', element: RoleListScreen },
  { path: '/rbac/roles/new', title: 'Yeni Rol', group: 'RBAC', parent: '/rbac/roles', element: CreateRoleScreen },
  { path: '/rbac/roles/edit/:id', title: 'Rol Düzenle', group: 'RBAC', parent: '/rbac/roles', element: EditRoleScreen },
  { path: '/rbac/system-users', title: 'Sistem Kullanıcıları', group: 'RBAC', element: SystemUsersScreen },
  { path: '/rbac/audit-logs', title: 'Denetim Kayıtları', group: 'RBAC', element: AuditLogsScreen },
  { path: '/users', title: 'Kullanıcılar', element: UserListScreen },
  { path: '/users/list', title: 'Kullanıcı Listesi', group: 'Kullanıcılar', element: UserListScreen },
  { path: '/users/details/:id', title: 'Kullanıcı Detayları', group: 'Kullanıcılar', parent: '/users/list', element: UserDetailsScreen },
  { path: '/contracts', title: 'Mehir Sözleşmeleri', group: 'Sözleşmeler', element: ContractListScreen },
  { path: '/contracts/list', title: 'Mehir Sözleşmeleri', group: 'Sözleşmeler', element: ContractListScreen },
  { path: '/contracts/details/:id', title: 'Sözleşme Detayları', group: 'Sözleşmeler', parent: '/contracts/list', element: ContractDetailsScreen },
  { path: '/contracts/savings-plan/:id', title: 'Birikim Planı', group: 'Sözleşmeler', parent: '/contracts/list', element: SavingsPlanScreen },
  { path: '/contracts/savings-plans', title: 'Birikim Planları', group: 'Sözleşmeler', element: SavingsPlansListScreen, pending: true },
  { path: '/campaigns', title: 'Kampanya Listesi', group: 'Kampanyalar', element: CampaignListScreen },
  { path: '/campaigns/list', title: 'Kampanya Listesi', group: 'Kampanyalar', element: CampaignListScreen },
  { path: '/campaigns/new', title: 'Yeni Kampanya', group: 'Kampanyalar', parent: '/campaigns/list', element: CreateCampaignScreen },
  { path: '/campaigns/codes/:id', title: 'Kampanya Kodları', group: 'Kampanyalar', parent: '/campaigns/list', element: CampaignCodesScreen },
  { path: '/campaigns/details/:id', title: 'Kampanya Detayları', group: 'Kampanyalar', parent: '/campaigns/list', element: CampaignDetailsScreen },
  { path: '/campaigns/edit/:id', title: 'Kampanyayı Düzenle', group: 'Kampanyalar', parent: '/campaigns/list', element: EditCampaignScreen },
  { path: '/referrals/list', title: 'Davet Listesi', group: 'Davetler', element: ReferralListScreen },
  { path: '/referrals/details/:id', title: 'Davet Detayları', parent: '/referrals/list', element: ReferralDetailsScreen },
  { path: '/referrals/manual-payment', title: 'Manuel Ödeme', group: 'Davetler', element: ManualPaymentScreen, pending: true },
  { path: '/referrals/earnings', title: 'Kullanıcı Hakedişleri', group: 'Davetler', element: UserEarningsScreen },
  { path: '/referrals/earnings/:userId', title: 'Hakediş Detayları', parent: '/referrals/earnings', element: EarningsBreakdownScreen },
  { path: '/content/blog/list', title: 'Blog Yazıları', group: 'İçerik', element: BlogListScreen },
  { path: '/content/blog/new', title: 'Yeni Blog Yazısı', group: 'İçerik', parent: '/content/blog/list', element: CreateBlogScreen },
  { path: '/content/blog/edit/:id', title: 'Düzenle', group: 'İçerik', parent: '/content/blog/list', element: EditBlogScreen },
  { path: '/content/blog/featured', title: 'Ana Sayfa Önceliklendirme', group: 'İçerik', parent: '/content/blog/list', element: FeaturedManagementScreen },
  { path: '/content/news/list', title: 'Bizden Haberler', group: 'İçerik', element: NewsListScreen },
  { path: '/content/news/new', title: 'Yeni Haber', group: 'İçerik', parent: '/content/news/list', element: CreateNewsScreen },
  { path: '/content/news/edit/:id', title: 'Düzenle', group: 'İçerik', parent: '/content/news/list', element: EditNewsScreen },
  { path: '/fit/videos', title: 'Video Listesi', group: 'Fit', element: VideoListScreen },
  { path: '/fit/videos/new', title: 'Yeni Video', group: 'Fit', parent: '/fit/videos', element: CreateVideoScreen },
  { path: '/fit/videos/edit/:id', title: 'Düzenle', group: 'Fit', parent: '/fit/videos', element: EditVideoScreen },
  { path: '/fit/exercises', title: 'Antrenman Listesi', group: 'Fit', element: ExerciseListScreen },
  { path: '/fit/exercises/new', title: 'Yeni Antrenman', group: 'Fit', parent: '/fit/exercises', element: CreateExerciseScreen },
  { path: '/fit/exercises/edit/:id', title: 'Düzenle', group: 'Fit', parent: '/fit/exercises', element: EditExerciseScreen },
  { path: '/fit/user-exercises', title: 'Kullanıcı Antrenman Listesi', group: 'Fit', element: UserExerciseListScreen, pending: true },
  { path: '/fit/user-exercises/details/:id', title: 'Detaylar', group: 'Fit', parent: '/fit/user-exercises', element: UserExerciseDetailsScreen, pending: true },
  { path: '/fit/targets', title: 'Hedefler', group: 'Fit', element: TargetListScreen },
  { path: '/fit/targets/new', title: 'Yeni', group: 'Fit', parent: '/fit/targets', element: CreateTargetScreen },
  { path: '/fit/targets/edit/:id', title: 'Düzenle', group: 'Fit', parent: '/fit/targets', element: EditTargetScreen },
  { path: '/fit/levels', title: 'Seviyeler', group: 'Fit', element: LevelListScreen },
  { path: '/fit/levels/new', title: 'Yeni', group: 'Fit', parent: '/fit/levels', element: CreateLevelScreen },
  { path: '/fit/levels/edit/:id', title: 'Düzenle', group: 'Fit', parent: '/fit/levels', element: EditLevelScreen },
  { path: '/fit/exercise-types', title: 'Egzersiz Tipleri', group: 'Fit', element: ExerciseTypeListScreen },
  { path: '/fit/exercise-types/new', title: 'Yeni', group: 'Fit', parent: '/fit/exercise-types', element: CreateExerciseTypeScreen },
  { path: '/fit/exercise-types/edit/:id', title: 'Düzenle', group: 'Fit', parent: '/fit/exercise-types', element: EditExerciseTypeScreen },
  { path: '/finance/statistics', title: 'İstatistikler', group: 'Finans', element: FinanceStatisticsScreen },
  { path: '/finance/news-management', title: 'Haber Yönetimi', group: 'Finans', element: FinanceNewsManagementScreen, pending: true },
  { path: '/settings/faq-categories', title: 'FAQ Kategorileri', group: 'Ayarlar', element: FaqCategoriesScreen },
  { path: '/settings/faq-management', title: 'FAQ Yönetimi', group: 'Ayarlar', element: FaqManagementScreen },
  { path: '/settings/communication-permissions', title: 'İletişim İzinleri', group: 'Ayarlar', element: CommunicationPermissionsScreen },
  { path: '/settings/push-notification', title: 'Push Notification', group: 'Ayarlar', element: PushNotificationScreen, pending: true },
  { path: '/settings/app-contracts', title: 'Sözleşmeler', group: 'Ayarlar', element: AppContractsScreen },
  { path: '/settings/contact-info', title: 'İletişim Bilgileri', group: 'Ayarlar', element: ContactInfoScreen },
  { path: '/profile', title: 'Profil', element: ProfileScreen },
  { path: '/changelog', title: 'Sürüm Notları', element: ChangelogScreen },
];

const byPath = new Map(APP_ROUTES.map((route) => [route.path, route]));

/** Longest-first so `/campaigns/new` is tested before `/campaigns`. */
const ROUTES_BY_SPECIFICITY = [...APP_ROUTES].sort(
  (a, b) => b.path.split('/').length - a.path.split('/').length
);

export const routeFor = (pathname: string): AppRoute | undefined =>
  ROUTES_BY_SPECIFICITY.find((route) => matchPath(route.path, pathname));

export interface Breadcrumb {
  title: string;
  href?: string;
}

/**
 * Breadcrumb trail for a pathname: home, the grouping label, each ancestor as a
 * link, then the page itself. Returns an empty trail for the dashboard, which
 * renders without breadcrumbs.
 */
export const breadcrumbsFor = (pathname: string): Breadcrumb[] => {
  const route = routeFor(pathname);
  if (!route || route.path === '/' || route.path === '/dashboard') return [];

  const ancestors: AppRoute[] = [];
  for (let current = route.parent; current; ) {
    const ancestor = byPath.get(current);
    if (!ancestor || ancestors.includes(ancestor)) break;
    ancestors.unshift(ancestor);
    current = ancestor.parent;
  }

  return [
    { title: 'Ana Sayfa', href: '/' },
    ...(route.group ? [{ title: route.group }] : []),
    ...ancestors.map((ancestor) => ({ title: ancestor.title, href: ancestor.path })),
    { title: route.title },
  ];
};
